import {makeAutoObservable} from "mobx"
import {User} from "oidc-client-ts"
import {AuthContextProps} from "react-oidc-context"
import {oidc, page} from "../config"
import {socketStore} from "./SocketStore";
import {tenantStore} from "./TenantStore";
import {userPrinterPreferenceStore} from "./ums";


export interface JWT {
    realm_access: { roles: string[] }
    tenantId?: string | undefined
}

export function getJwt(token: string): JWT {
    return JSON.parse(atob(token.split('.')[1])) as JWT
}

class AuthStore {
    get jwt(): JWT | undefined {
        return this._jwt;
    }

    set jwt(value: JWT | undefined) {
        this._jwt = value;
    }
    get auth(): AuthContextProps | undefined {
        return this._auth
    }

    get accessToken(): string | undefined {
        return this.auth?.user?.access_token
    }

    set auth(value: AuthContextProps | undefined) {
        this._auth = value
        const accessToken = value?.user?.access_token
        if (accessToken) {
            this.jwt = getJwt(accessToken)
            // tenantSelectStore.updateToken(value?.user?.access_token)
            // featureStore.load()
            userPrinterPreferenceStore.load().finally()
            socketStore.build(accessToken)
        }
    }

    hasRole(role: string): boolean {
        const {jwt} = this
        if (jwt) {
            const roles = jwt.realm_access?.roles || []
            return roles.indexOf(role) !== -1
        }
        return false
    }

    get isAdmin(): boolean {
        const accessToken = this._auth?.user?.access_token
        if (accessToken) {
            const jwt = getJwt(accessToken)
            if (jwt) {
                return jwt.realm_access.roles.indexOf("superadmin") !== -1 || jwt.realm_access.roles.indexOf("admin") !== -1
            }

        }
        return false
    }

    private _auth: AuthContextProps | undefined = undefined

    constructor() {
        makeAutoObservable(this)
    }

    get oidcStorage(): string | null {
        const authority = tenantStore.config?.authority
        if (!authority) {
            return null
        }

        const oidcStorage = localStorage.getItem(`oidc.user:${authority}:${oidc.config.client_id}`)
        if (!oidcStorage) {
            return null
        }
        return oidcStorage
    }

    get user(): User | undefined {
        const oidcStorage = this.oidcStorage
        if (oidcStorage !== null) {
            return User.fromStorageString(oidcStorage)
        }
        return undefined
    }

    get initials(): string {
        const user = this.user
        if (user) {

            const familyName = user.profile.family_name
            const givenName = user.profile.given_name
            if (!familyName && !givenName) {
                return user.profile.sub.substring(0, 2)
            }
            if (givenName && familyName) {
                return `${givenName.substring(0, 1) || ' -'} ${familyName.substring(0, 1) || ''}`
            }
        }
        return 'U'
    }

    get name(): string | undefined {
        const user = this.user
        if (user) {

            const familyName = user.profile.family_name
            const givenName = user.profile.given_name
            if (givenName && familyName) {
                return `${givenName} ${familyName}`
            }
        }
        return undefined
    }

    private _jwt: JWT | undefined

    clear() {
        this.auth?.removeUser()
        oidc.userStateStore.getAllKeys().then(keys => {
            keys.forEach(key => {
                oidc.userStateStore.remove(key)
            })
        })
    }

    logout() {
        this.clear()
        this.auth?.signoutRedirect({
            post_logout_redirect_uri: page.baseUrl
        })
    }
}

const authStore = new AuthStore()

export {
    authStore
}
